import { useState } from "react";
import styled from "styled-components/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import SidePanel from "../SidePanel";
import {
  BoxHeader,
  BoxNavLeft,
  BoxTitle,
  BoxContent,
  BoxNavRight,
  BoxNavButton,
  Box,
  BoxFooter,
} from "../Box";
import { Close, CalendarClock, TrashCan, Info } from "../Icons";
import ContactsWell from "../ContactsWell";
import { useFields } from "../../hooks/use-fields";
import Stack from "../Stack";
import Input, { Label } from "../Input";
import { DatePickerOverlay } from "../DatePicker";
import ms from "ms";
import Button, { IconButton } from "../Button";
import CellSet, { Row, ItemButton } from "../Cellset";
import Title from "../Title";
import FilePicker from "../FilePicker";
import { useSkipioApi, touchSkipioApi } from "../../api";
import useLoadAll from "../../hooks/use-load-all";
import CampaignSelector from "../CampaignSelector";
import Tooltip from "../Tooltip";
import { useToast } from "../../hooks/use-toast";
import Composer from "../Composer";
import useExitWarning from "../../hooks/use-prevent-exit";
import useMoment from "../../hooks/use-moment";
import { PostViewerSidePanel } from "../PostViewer";
import { WarningAlert } from "../Alert";
import usePlan from "../../hooks/use-plan";
import { RootAnchor } from "../Anchor";
import { useSkipioQuery } from "../../hooks/use-query";
import { fetchCampaign } from "../../api/campaigns";

export default function EventEditor({
  hide,
  eventId,
  setEventId,
  contactIds = [],
  popToTop = hide,
}) {
  const moment = useMoment();
  const [errors, setErrors] = useState({});
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const eventQuery = useSkipioQuery({
    queryKey: ["events", eventId],
    enabled: !!eventId,
  });
  const event = eventId ? eventQuery.data : {};

  const { data: eventContacts = [] } = useLoadAll(
    eventId ? skipioApi.eventContacts : undefined,
    {
      eventId,
      status: "current",
    },
    true
  );

  const { field, value, set, isChanged, checkpoint, reset } = useFields({
    initialValue: {
      recipients: {
        contactIds: [...contactIds, ...eventContacts.map((ec) => ec.contactId)],
        groupIds: [],
      },
      name: event.name || "",
      description: event.description || "",
      startDate: event.startDate || new Date(Date.now() + ms("24h")),
      endDate: event.endDate || new Date(Date.now() + ms("25h")),
      streetAddress: event.streetAddress || "",
      city: event.city || "",
      state: event.state || "",
      zipCode: event.zipCode || "",
      campaignId: event.campaignId || null,
      hostName: event.hostName || "",
      imageUrl: event.imageUrl || "",
    },
  });

  useExitWarning(isChanged);

  const campaignQuery = useQuery({
    queryKey: ["campaigns", value.campaignId],
    queryFn: () => fetchCampaign(value.campaignId),
    enabled: !!value.campaignId,
    suspense: false,
    useErrorBoundary: true,
  });
  const campaign = value.campaignId ? campaignQuery.data : null;

  const plan = usePlan();
  const isOver =
    plan.maxContactsInEvent &&
    plan.maxContactsInEvent < value.recipients.contactIds.length;

  const showToast = useToast();

  async function save() {
    if (isOver) return;
    if (!isChanged) return;
    setErrors({});

    const payload = {
      event: {
        ...value,
        recipients: undefined,
        startDate: eventId
          ? undefined
          : new Date(value.startDate).toISOString(),
        endDate: eventId ? undefined : new Date(value.endDate).toISOString(),
      },
      contactIds: value.recipients.contactIds,
    };

    const caller = eventId
      ? skipioApi.events[eventId].put
      : skipioApi.events.post;

    let event = null;
    try {
      const {
        data: { data },
      } = await caller(
        {},
        {
          data: payload,
        }
      );

      event = data;

      if (eventId) {
        await skipioApi.eventContacts.put(
          {},
          { data: { eventId, contactId: value.recipients.contactIds } }
        );
      }
    } catch (e) {
      if (e.response) {
        setErrors(e.response.data);
        return;
      } else throw e;
    }

    showToast(
      eventId ? "Event updated successfully" : "Event created successfully",
      { success: true }
    );
    checkpoint();

    // Change to viewer screen for new events
    if (!eventId) setEventId(event.id);
    else hide();

    await touchSkipioApi("events", "eventContacts");
    await queryClient.invalidateQueries({ queryKey: ["events"] });
    await queryClient.invalidateQueries({ queryKey: ["eventContacts"] });
  }

  return (
    <>
      <BoxHeader>
        <BoxNavLeft>
          <BoxTitle>Event</BoxTitle>
        </BoxNavLeft>
        <BoxNavRight>
          <PostViewerSidePanel
            articleId="94c09821-c529-43c6-b62d-493925c7554a"
            trigger={({ show, ref }) => (
              <BoxNavButton onClick={show} ref={ref}>
                <Info /> <span>Show Me How</span>
              </BoxNavButton>
            )}
          />
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent style={{ padding: 0 }}>
        <BoxContent shrink>
          <Stack>
            <ContactsWell {...field("recipients")} />
            {isOver && (
              <WarningAlert>
                Your plan allows for {plan.maxContactsInEvent} contacts in an
                event. <RootAnchor to="/app/plan">Upgrade your plan</RootAnchor>{" "}
                to have more.
              </WarningAlert>
            )}
          </Stack>
        </BoxContent>
        <Stack>
          <BoxHeader white>
            <BoxTitle>Event Details</BoxTitle>
          </BoxHeader>
          <BoxContent shrink>
            <Stack>
              <Input
                {...field("name")}
                label="Title"
                required
                error={errors.name}
              />
              <Input
                {...field("description")}
                type="textarea"
                label="Description"
                error={errors.description}
                helpText="Your contacts will see this description on the RSVP page."
              />
              {!eventId ? (
                <Stack horizontal>
                  <DatePickerOverlay
                    bottom={false}
                    {...field("startDate")}
                    onChange={(applyPatch) => {
                      field("startDate").onChange((startDate) => {
                        const newDate = applyPatch(startDate);
                        // apply the start date day to the end date
                        const newEndDate = new Date(value.endDate);
                        newEndDate.setFullYear(newDate.getFullYear());
                        newEndDate.setMonth(newDate.getMonth());
                        newEndDate.setDate(newDate.getDate());
                        set("endDate", newEndDate);
                        set("startDate", newDate);
                      });
                    }}
                    error={errors.startDate}
                    trigger={({ ref, show, isShowing }) => (
                      <Input
                        onFocus={show}
                        focused={isShowing}
                        ref={ref}
                        label="Starts at"
                        readOnly
                        value={moment(value.startDate).format("llll")}
                        required
                      />
                    )}
                  />
                  <DatePickerOverlay
                    bottom={false}
                    minDate={value.endDate + ms("5m")}
                    right
                    {...field("endDate")}
                    error={errors.endDate}
                    mode="time"
                    trigger={({ ref, show, isShowing }) => (
                      <Input
                        onFocus={show}
                        focused={isShowing}
                        ref={ref}
                        label="Ends at"
                        readOnly
                        value={moment(value.endDate).format("llll")}
                        required
                      />
                    )}
                  />
                </Stack>
              ) : (
                <>
                  <Label>Date &amp; Time</Label>
                  <Box>
                    <BoxContent>
                      <Stack>
                        <Title>
                          {moment(value.startDate).format("ddd, MMMM D, YYYY")}
                          <small>{`${moment(value.startDate).format(
                            "LT"
                          )} - ${moment(value.endDate).format("LT")}`}</small>
                        </Title>

                        <P>
                          To reschedule,{" "}
                          <SidePanel
                            trigger={({ ref, show }) => (
                              <Button
                                variant="link"
                                ref={ref}
                                onClick={show}
                                style={{
                                  padding: 0,
                                  textTransform: "initial",
                                  fontWeight: "inherit",
                                }}
                              >
                                cancel this event
                              </Button>
                            )}
                            content={({ hide: hideCancel }) => (
                              <CancelEvent
                                hide={hideCancel}
                                onCancelEvent={() => {
                                  hideCancel();
                                  hide();
                                  popToTop();
                                }}
                                eventId={eventId}
                              />
                            )}
                          />{" "}
                          and create a new one.{" "}
                        </P>
                      </Stack>
                    </BoxContent>
                  </Box>
                </>
              )}
              <Input {...field("streetAddress")} label="Street Address" />
              <Stack horizontal>
                <Input {...field("city")} label="City" />
                <Input {...field("state")} label="State" />
                <Input {...field("zipCode")} label="Zip Code" />
              </Stack>
            </Stack>
          </BoxContent>
          <BoxHeader white>
            <BoxTitle>Host/Hostess Details</BoxTitle>
          </BoxHeader>
          <BoxContent shrink>
            <Stack>
              <Input {...field("hostName")} label="Host/Hostess Name" />
              <FilePicker
                accept={(mime) => mime.includes("image")}
                options={{
                  transformations: {
                    crop: {
                      aspectRatio: 21 / 9,
                      force: true,
                    },
                  },
                }}
                onChange={({ url }) => {
                  set("imageUrl", url);
                }}
                trigger={({ show }) => (
                  <Input
                    readOnly
                    value={value.imageUrl}
                    label="Event Image"
                    onFocus={show}
                  />
                )}
              />

              {Boolean(value.imageUrl) && <Img src={value.imageUrl} />}
            </Stack>
          </BoxContent>
          <BoxHeader white>
            <BoxTitle>Reminder Campaign</BoxTitle>
          </BoxHeader>
          <BoxContent shrink>
            <Label>Reminder Campaign</Label>
            <Stack>
              {Boolean(campaign) && (
                <Box>
                  <BoxHeader>
                    <BoxTitle>{campaign.name}</BoxTitle>
                    <BoxNavRight style={{ paddingRight: "8px" }}>
                      <Tooltip label="Remove campaign from event">
                        <IconButton onClick={() => set("campaignId", null)}>
                          <TrashCan />
                        </IconButton>
                      </Tooltip>
                    </BoxNavRight>
                  </BoxHeader>
                </Box>
              )}
              {
                <CellSet
                  style={{
                    display: campaign ? "none" : undefined,
                  }}
                >
                  <Row>
                    <CampaignSelector
                      campaignType="reminder"
                      title="Select a reminder campaign"
                      {...field("campaignId")}
                      trigger={({ ref, show }) => (
                        <ItemButton ref={ref} onClick={show}>
                          <CalendarClock />
                          <Title>
                            Select Reminder Campaign
                            <small>
                              Use an existing reminder campaign with this event
                            </small>
                          </Title>
                        </ItemButton>
                      )}
                    />
                  </Row>
                  <Row>
                    <CampaignSelector
                      campaignType="reminder"
                      title="Select a reminder campaign to customize"
                      clone
                      {...field("campaignId")}
                      trigger={({ ref, show }) => (
                        <ItemButton ref={ref} onClick={show}>
                          <CalendarClock />
                          <Title>
                            Customize Reminder Campaign
                            <small>
                              Customize an existing reminder campaign to use
                              only for this event
                            </small>
                          </Title>
                        </ItemButton>
                      )}
                    />
                  </Row>
                </CellSet>
              }
            </Stack>
          </BoxContent>
        </Stack>
      </BoxContent>
      <BoxFooter>
        {isChanged && (
          <BoxNavLeft>
            <Button variant="link" onClick={reset}>
              Cancel
            </Button>
          </BoxNavLeft>
        )}
        <BoxNavRight>
          {eventId && (
            <SidePanel
              trigger={({ ref, show }) => (
                <Button variant="link" ref={ref} onClick={show}>
                  Cancel Event
                </Button>
              )}
              content={({ hide: hideCancel }) => (
                <CancelEvent
                  hide={hide}
                  eventId={eventId}
                  onCancelEvent={() => {
                    hideCancel();
                    hide();
                    popToTop();
                  }}
                />
              )}
            />
          )}
          <Button onClick={save} disabled={!isChanged || isOver}>
            Save
          </Button>
        </BoxNavRight>
      </BoxFooter>
    </>
  );
}

function CancelEvent({ hide, eventId, onCancelEvent }) {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();

  const [value, onChange] = useState({
    body: "",
  });

  const showToast = useToast();

  async function cancelEvent(skipNotify) {
    await skipioApi.events[eventId].delete({ skipNotify, message: value.body });
    showToast("Event canceled", { success: true });
    onCancelEvent();
    await touchSkipioApi("events");
    await queryClient.invalidateQueries({ queryKey: ["events"] });
  }

  return (
    <>
      <BoxHeader>
        <BoxTitle>Cancel Event</BoxTitle>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent>
        <Box elevated>
          <BoxContent>
            <Stack>
              <Title>
                Cancellation Message
                <small>
                  Enter a cancellation message that will automatically get sent
                  to the event attendees.
                </small>
              </Title>
              <>
                <Label>Message Body</Label>
                <Composer
                  positionOverlaysBeneath
                  value={value}
                  onChange={onChange}
                  enableMedia={false}
                  enableRinglessVoicemail={false}
                  enableMessageLibrary={false}
                  enableScheduling={false}
                />
              </>
            </Stack>
          </BoxContent>
        </Box>
      </BoxContent>
      <BoxFooter>
        <BoxNavLeft>
          <Button variant="link" onClick={hide}>
            Back
          </Button>
        </BoxNavLeft>
        <BoxNavRight>
          <Button
            variant="link"
            onClick={() => {
              cancelEvent(true);
            }}
          >
            Skip &amp; Finish
          </Button>
          <Button
            onClick={() => {
              cancelEvent(false);
            }}
          >
            Send &amp; Finish
          </Button>
        </BoxNavRight>
      </BoxFooter>
    </>
  );
}

const P = styled.p`
  color: #676767;
  line-height: 1.6em;
`;

const Img = styled.img`
  border-radius: 7px;
  width: 100%;
  height: 238px;
`;
